import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * Google Tag Manager Service
 *
 * Injects GTM scripts into the page at runtime.
 * This approach allows the GTM ID to be configured via environment files
 * and works correctly with SSR (only runs in browser).
 *
 * Usage:
 * - GTM ID is configured in environment.ts / environment.prod.ts
 * - Service is initialized via APP_INITIALIZER in app.config.ts
 * - No manual script tags needed in index.html
 */
@Injectable({ providedIn: 'root' })
export class GtmService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private initialized = false;

  /**
   * Initialize GTM by injecting the required scripts.
   * Safe to call multiple times - will only execute once.
   */
  initialize(): void {
    // Only run in browser environment (SSR safety)
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Prevent double initialization
    if (this.initialized) {
      return;
    }

    const gtmId = environment.gtm?.id;
    if (!gtmId) {
      // Only warn on localhost — deployed dev intentionally has no GTM ID,
      // so the warning would just be console noise there.
      if (this.document.defaultView?.location.hostname === 'localhost') {
        console.warn('[GTM] No GTM ID configured in environment. Skipping initialization.');
      }
      return;
    }

    // Mark initialized synchronously so duplicate initialize() calls no-op,
    // even before the deferred injection runs.
    this.initialized = true;

    // Pre-seed the dataLayer queue so any pushEvent() call that fires before
    // gtm.js loads (e.g. early NavigationEnd page-view) still gets buffered;
    // gtm.js will pick it up when it finishes loading.
    const win = this.document.defaultView as Window & { dataLayer?: unknown[] };
    if (win) {
      win.dataLayer = win.dataLayer || [];
    }

    // Defer script injection past LCP. Without this, gtm.js (~113 KiB) parses
    // on the main thread during the LCP window, dragging both LCP and TBT.
    // We accept that any third-party tag firing in the ~1-3s deferral window
    // is missed on first page load — fine for analytics, the heavy events
    // happen well after.
    this.deferToIdle(() => {
      this.injectHeadScript(gtmId);
      this.injectNoScript(gtmId);
      if (!environment.production) {
        console.log(`[GTM] Initialized with container ID: ${gtmId}`);
      }
    });
  }

  /**
   * Run `fn` after the browser is idle (post-LCP). Falls back to a 1.5s
   * timeout when requestIdleCallback isn't available (Safari < 18).
   */
  private deferToIdle(fn: () => void): void {
    const win = this.document.defaultView as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    };
    if (win?.requestIdleCallback) {
      win.requestIdleCallback(() => fn(), { timeout: 3000 });
    } else {
      win?.setTimeout(fn, 1500);
    }
  }

  /**
   * Inject the GTM script into <head>
   */
  private injectHeadScript(gtmId: string): void {
    const script = this.document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;

    // Insert as first child of <head> for earliest execution
    const head = this.document.head;
    if (head.firstChild) {
      head.insertBefore(script, head.firstChild);
    } else {
      head.appendChild(script);
    }
  }

  /**
   * Inject the GTM noscript iframe as first child of <body>
   */
  private injectNoScript(gtmId: string): void {
    const noscript = this.document.createElement('noscript');
    const iframe = this.document.createElement('iframe');

    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscript.appendChild(iframe);

    // Insert as first child of <body>
    const body = this.document.body;
    if (body.firstChild) {
      body.insertBefore(noscript, body.firstChild);
    } else {
      body.appendChild(noscript);
    }
  }

  /**
   * Push an event to the GTM dataLayer
   */
  pushEvent(event: Record<string, unknown>): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const win = this.document.defaultView as Window & { dataLayer?: unknown[] };
    if (win?.dataLayer) {
      win.dataLayer.push(event);
    }
  }
}
